import { Badge, Button, Card, Divider, Icon } from '@ds/core';
import { Modal } from '@ds/feedback';
import { moeda } from '@/comuns/formato';
import { quantidade, unidadeAbrev, unidadeLabel } from '@/comuns/unidades';
import type { Procedimento } from '@/data/tipos';
import { SITUACAO } from '@/screens/estoque/ProdutoDetalhe';

export interface DetalheDoProcedimentoProps {
    procedimento: Procedimento | null;
    onClose: () => void;
    onEditar: (procedimento: Procedimento) => void;
    onReservar: (procedimento: Procedimento) => void;
}

/**
 * Ficha do procedimento.
 *
 * Modal, e não gaveta: conferir insumo e margem é consulta pontual — abrir,
 * olhar, fechar —, e não trabalho lado a lado com a tabela.
 */
export function DetalheDoProcedimento({ procedimento, onClose, onEditar, onReservar }: DetalheDoProcedimentoProps) {
    if (!procedimento) return null;

    const custo = procedimento.custoDeInsumosCentavos + procedimento.custoExtraCentavos;

    return (
        <Modal
            open onClose={onClose} width={620}
            title={procedimento.nome}
            description={[procedimento.categoria, `${procedimento.duracaoMin} min`, moeda(procedimento.precoCentavos)].filter(Boolean).join(' · ')}
            footer={
                <>
                    <Button variant="ghost" iconLeft={<Icon name="pencil" size={15} />} onClick={() => onEditar(procedimento)}>Editar</Button>
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="secondary" onClick={onClose}>Fechar</Button>
                    <Button
                        iconLeft={<Icon name="package-check" size={15} />}
                        disabled={procedimento.insumos.length === 0}
                        onClick={() => onReservar(procedimento)}
                    >
                        Reservar insumos
                    </Button>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Card title="Insumos utilizados" subtitle="Debitados do estoque na baixa da reserva" padding={16}>
                    {procedimento.insumos.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Este procedimento não consome insumos.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {procedimento.insumos.map((i) => (
                                <div key={i.produtoId} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ display: 'block', fontWeight: 500 }}>
                                            {quantidade(i.quantidade)} {unidadeAbrev(i.unidadeConsumo)} · {i.produto}
                                        </span>
                                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-tertiary)' }}>
                                            {quantidade(i.disponivel)} {unidadeLabel(i.disponivel, i.unidade)} em estoque
                                        </span>
                                    </span>
                                    <Badge tone={SITUACAO[i.situacao].tom} dot>{SITUACAO[i.situacao].rotulo}</Badge>
                                    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(i.custoCentavos)}</span>
                                        {/* Quanto isso é de embalagem: 50 UI de um frasco
                                            de 100 são meio frasco, e é meio frasco que sai
                                            do armário. */}
                                        <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                                            {quantidade(i.embalagens)} {unidadeLabel(i.embalagens, i.unidade)}
                                        </span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {procedimento.custosExtras.length > 0 && (
                        <>
                            <Divider label="Custos extras" spacing={4} />
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {procedimento.custosExtras.map((c) => (
                                    <div key={c.nome} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                        <span style={{ color: 'var(--text-secondary)' }}>
                                            {quantidade(c.quantidade)} {unidadeLabel(c.quantidade, c.unidade)} · {c.nome}
                                        </span>
                                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(c.quantidade * c.valorCentavos)}</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <Divider spacing={4} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Custo total</span>
                        <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{moeda(custo)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Margem</span>
                        <span style={{ fontWeight: 600 }}>
                            {procedimento.precoCentavos === 0 ? 'sem preço' : `${procedimento.margem}% · ${moeda(procedimento.precoCentavos - custo)}`}
                        </span>
                    </div>
                </Card>

                <Card title="Execução" padding={16}>
                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13 }}>
                        <dt style={{ color: 'var(--text-secondary)' }}>Duração</dt>
                        <dd style={{ margin: 0, fontWeight: 500 }}>{procedimento.duracaoMin} minutos</dd>
                        <dt style={{ color: 'var(--text-secondary)' }}>Profissionais</dt>
                        <dd style={{ margin: 0, fontWeight: 500 }}>
                            {procedimento.profissionais.length ? procedimento.profissionais.map((p) => p.nome).join(' · ') : 'sem vínculo'}
                        </dd>
                        <dt style={{ color: 'var(--text-secondary)' }}>Estoque suporta</dt>
                        <dd style={{ margin: 0, fontWeight: 500 }}>
                            {procedimento.execucoesPossiveis === null
                                ? 'sem restrição — não consome insumo'
                                : `${procedimento.execucoesPossiveis} ${procedimento.execucoesPossiveis === 1 ? 'execução' : 'execuções'}`}
                        </dd>
                        {procedimento.reconsultaDias > 0 && (
                            <>
                                <dt style={{ color: 'var(--text-secondary)' }}>Reconsulta</dt>
                                <dd style={{ margin: 0, fontWeight: 500 }}>em {procedimento.reconsultaDias} dias</dd>
                            </>
                        )}
                    </dl>
                </Card>

                {(procedimento.descricao || procedimento.camposPersonalizados.length > 0) && (
                    <Card title="Informações adicionais" padding={16}>
                        {procedimento.descricao && (
                            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)' }}>{procedimento.descricao}</p>
                        )}
                        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13 }}>
                            {procedimento.camposPersonalizados.map((c) => (
                                <div key={c.rotulo} style={{ display: 'contents' }}>
                                    <dt style={{ color: 'var(--text-secondary)' }}>{c.rotulo}</dt>
                                    <dd style={{ margin: 0, fontWeight: 500 }}>{c.valor}</dd>
                                </div>
                            ))}
                        </dl>
                    </Card>
                )}
            </div>
        </Modal>
    );
}
